// Saving and deleting vet visits, with everything they touch (see
// src/domain/vetvisit.ts).
//
// A save only writes the records whose visit details actually changed, and
// merges onto what's there — so a reminder you edited, ticked off or deleted
// in the care plan stays that way when you fix a typo in the visit's notes.

import { derivedSignature, possibleVisitIds, visitEffects } from '../domain/vetvisit.ts';
import { toISODate } from '../domain/time.ts';
import type { VetVisit } from '../domain/types.ts';
import { markCareDone, reopenCare, saveProfile } from './actions.ts';
import { data } from './index.ts';

/** Save a visit and apply its effects. Returns how many care jobs were newly ticked off. */
export function saveVetVisit(v: VetVisit, previous?: VetVisit, answeredQuestions: string[] = []): number {
  const { store } = data();
  const profile = store.get('profile', 'arlo');
  store.put('vetVisits', v);

  const eff = visitEffects(v, profile);
  const before = previous ? visitEffects(previous, profile) : undefined;
  const prevEvent = new Map(before?.events.map((e) => [e.id, derivedSignature(e)]));
  const prevCare = new Map(before?.care.map((c) => [c.id, derivedSignature(c)]));

  // Records the visit no longer wants (a removed medicine, a cleared follow-up).
  const possible = possibleVisitIds(v, previous);
  for (const id of possible.events) if (!eff.eventIds.includes(id) && store.get('events', id)) store.remove('events', id);
  for (const id of possible.care) {
    if (eff.careIds.includes(id)) continue;
    const c = store.get('care', id);
    if (c && !c.done) store.remove('care', id);
  }

  // New or changed ones only; unchanged ones are left exactly as they are (even if deleted since).
  for (const e of eff.events) {
    if (prevEvent.get(e.id) === derivedSignature(e)) continue;
    const cur = store.get('events', e.id);
    store.put('events', cur ? ({ ...cur, ...e } as typeof e) : e);
  }
  for (const c of eff.care) {
    if (prevCare.get(c.id) === derivedSignature(c)) continue;
    const cur = store.get('care', c.id);
    store.put('care', cur ? { ...cur, kind: c.kind, title: c.title, due: c.due, why: c.why } : c);
  }

  // Care-plan items ticked or unticked at this visit (repeats book / un-book their next date).
  const was = new Set(previous?.careDone ?? []);
  const now = new Set(v.careDone ?? []);
  let newlyDone = 0;
  for (const id of now) {
    if (was.has(id)) continue;
    const item = store.get('care', id);
    if (item && !item.done) {
      markCareDone(item, toISODate(v.at));
      newlyDone++;
    }
  }
  for (const id of was) if (!now.has(id)) reopenCare(id);

  // Questions answered at the visit come off the list.
  if (answeredQuestions.length && profile?.vetQuestions?.length) saveProfile({ vetQuestions: profile.vetQuestions.filter((q) => !answeredQuestions.includes(q)) });
  return newlyDone;
}

/** Delete a visit and undo it: reopen what it ticked off and remove what it added. */
export function deleteVetVisit(v: VetVisit): void {
  const { store } = data();
  for (const id of v.careDone ?? []) reopenCare(id);
  const possible = possibleVisitIds(v);
  for (const id of possible.events) if (store.get('events', id)) store.remove('events', id);
  for (const id of possible.care) if (store.get('care', id)) store.remove('care', id);
  store.remove('vetVisits', v.id);
}
